"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { CLASS_CATEGORIES, IClassCard } from "@/sanity/pages/PageCourseGalleryTemplate";
import { ChevronIcon, MetaDot } from "./_shared";

interface AllClassesSectionProps {
  title?: string;
  filterLabel?: string;
  classes?: IClassCard[];
}

// Level pill palette — cool blue → amber → red, matching the accent used by the
// discipline bullets (#98D8FF) at the entry level.
const LEVEL_STYLES: Record<string, string> = {
  beginner: "text-[#98D8FF] bg-[#98D5F9]/10 border-[#6B9EBD]/20",
  intermediate: "text-[#F9D598] bg-[#F9D598]/10 border-[#BD9E6B]/20",
  advanced: "text-[#F99898] bg-[#F99898]/10 border-[#BD6B6B]/20",
};

// All Classes — Figma 3780:19611. 1202px container, three 16:9 covers per row on
// a 24px gutter with 18px between rows. The grid reads as bare covers; title,
// mentor, level and category only surface on hover.
export default function AllClassesSection({ title, filterLabel, classes }: AllClassesSectionProps) {
  const items = classes ?? [];

  // Only the categories actually in use, kept in the canonical schema order so
  // the dropdown reads the same regardless of how the classes are sorted.
  const categories = useMemo(() => {
    const used = new Set(items.map((item) => item.category?.trim()).filter(Boolean));
    return CLASS_CATEGORIES.filter((category) => used.has(category));
  }, [items]);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  // The filter stays inert until an editor tags at least two categories, which
  // is how the design draws it — a closed control with no options.
  const interactive = categories.length > 1;

  const visible = selected ? items.filter((item) => item.category?.trim() === selected) : items;

  if (items.length === 0) return null;

  return (
    <section className="relative z-[10] flex w-full justify-center bg-black px-[2rem] pt-[8rem] pb-[12rem] lg:px-[45rem] lg:pt-[16.5rem] lg:pb-[25rem]">
      <div className="flex w-[min(150rem,100%)] flex-col items-start gap-[2rem] lg:gap-[3rem]">
        {/* Heading row */}
        <div className="flex w-full items-end justify-between gap-[2rem]">
          {title && (
            <h2 className="m-0 font-news text-[3rem] leading-[1.1] tracking-[-0.06rem] text-white lg:text-[4rem] lg:tracking-[-0.08rem]">
              {title}
            </h2>
          )}

          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => interactive && setOpen((v) => !v)}
              aria-expanded={interactive ? open : undefined}
              aria-haspopup={interactive ? "listbox" : undefined}
              tabIndex={interactive ? 0 : -1}
              className={`flex flex-col items-center bg-white/10 border border-white/[0.08] rounded-[0.625rem] px-[1.75rem] py-[1.25rem] lg:px-[2.25rem] lg:py-[1.5rem] ${
                interactive ? "cursor-pointer transition hover:bg-white/[0.14]" : "cursor-default"
              }`}>
              <span className="flex w-[11rem] items-center justify-between lg:w-[13.625rem]">
                <span className="overflow-hidden text-left font-geist text-[2.25rem] leading-[1.1] text-ellipsis whitespace-nowrap text-white lg:text-[2.75rem]">
                  {selected ?? filterLabel ?? "All classes"}
                </span>
                <ChevronIcon
                  className={`text-white opacity-45 transition-transform duration-200 w-[1.875rem] h-[1.07rem] ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </span>
            </button>

            {interactive && open && (
              <div
                role="listbox"
                className="absolute top-[calc(100%+1rem)] right-0 z-20 flex min-w-full flex-col overflow-hidden rounded-[0.625rem] border border-white/10 bg-[#0F0F0F]">
                {[null, ...categories].map((category) => (
                  <button
                    key={category ?? "__all"}
                    type="button"
                    role="option"
                    aria-selected={selected === category}
                    onClick={() => {
                      setSelected(category);
                      setOpen(false);
                    }}
                    className={`text-left whitespace-nowrap transition font-geist px-[2.25rem] py-[1.5rem] text-[2.25rem] lg:text-[2.5rem] hover:bg-white/10 ${
                      selected === category ? "text-white" : "text-white/60"
                    }`}>
                    {category ?? filterLabel ?? "All classes"}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Cover grid */}
        <div className="grid w-full grid-cols-1 gap-x-[3.015rem] gap-y-[2.25rem] sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((item, i) => (
            <FadeIn
              key={`${item.name ?? "class"}-${i}`}
              delay={(i % 3) * 0.08}>
              <article className="group relative aspect-[16/9] w-full cursor-default overflow-hidden rounded-[1rem] border border-white/10 bg-white/[0.04]">
                {item.image && (
                  <Image
                    src={item.image.src}
                    alt={item.name ?? ""}
                    fill
                    quality={100}
                    sizes="(max-width: 640px) 90vw, (max-width: 992px) 45vw, 48rem"
                    placeholder={item.image.lqip ? "blur" : undefined}
                    blurDataURL={item.image.lqip}
                    className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                  />
                )}

                {/* Dim + detail overlay. Hover-only by design; devices without a
                    hover pointer get it permanently so the meta is never lost. */}
                <div className="absolute inset-0 flex flex-col justify-end gap-[1.25rem] bg-gradient-to-t from-black/90 via-black/60 to-black/30 p-[2rem] opacity-0 transition-opacity duration-300 ease-out group-hover:opacity-100 lg:p-[2.5rem] [@media(hover:none)]:opacity-100">
                  {item.level && (
                    <span
                      className={`flex absolute text-white items-center capitalize  font-jetbrainsmono top-[2rem] left-[2rem] text-[1.5rem] lg:top-[2.5rem] lg:left-[2.5rem] lg:text-[1.625rem] bg-black p-[.5rem]`}>
                      {item.level}
                    </span>
                  )}

                  {item.name && (
                    <h3 className="m-0 font-geist text-[2.5rem] leading-[1.2] tracking-[-0.05rem] text-white lg:text-[2.75rem]">
                      {item.name}
                    </h3>
                  )}

                  {(item.mentor || item.category) && (
                    <div className="flex flex-wrap items-center gap-[1.5rem]">
                      {item.mentor && (
                        <span className="font-geist text-[1.75rem] text-white/80 lg:text-[2rem]">{item.mentor}</span>
                      )}
                      {item.mentor && item.category && <MetaDot />}
                      {item.category && (
                        <span className="font-geist text-[1.5rem] text-white/60 uppercase lg:text-[1.75rem]">
                          {item.category}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </article>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}
